import { PaginationApiType } from '@shared/types/data-table.types';
import {
  ExportType,
  ExportWritePreference,
  FrequencyValues,
  PiiAttachPayload,
  ScheduledType,
} from '@shared/types/exports-types';
import {
  TradeDeskAccountRate,
  TradeDeskIdType,
} from '@shared/types/trade-desk-accounts.types';

export enum ExportManagerStatus {
  SCHEDULED = 'Scheduled',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
}

export enum JobStatus {
  RUNNING = 'Running',
  COMPLETED = 'Completed',
  FAILED = 'Failed',
  SCHEDULED = 'Scheduled',
}

export enum ExportFrequency {
  ONE_TIME = 'One Time',
  DAILY = 'Daily',
  WEEKLY = 'Weekly',
  MONTHLY = 'Monthly',
}

export enum ExportRecurrence {
  ONE_TIME = 'One Time',
  RECURRING = 'Recurring',
}

export interface ExportSummary {
  id: string;
  exportName: string;
  segmentName: string;
  destination: string;
  exportType: ExportRecurrence;
  startDate: string;
  endDate: string | null;
  lastRunDate: string;
  status: ExportManagerStatus;
  initiatedBy: string;
}

export interface GetExportSummariesResponse {
  exports: ExportSummary[];
  pagination: PaginationApiType;
}

export interface DestinationSchedule {
  frequency: ExportFrequency | string;
  time: string;
  startDate: string;
  endDate: string | null;
  nextRun: string | null;
  /** IANA timezone string provided by the backend (e.g. 'America/New_York'). Optional for backward compatibility with older exports. */
  timezone?: string;
}

export interface ExportedField {
  name: string;
  required: boolean;
}

export interface JobRun {
  jobId: string;
  status: JobStatus | string;
  started: string;
  completed: string | null;
  duration: string | null;
  records: number | null;
  /**
   * Amazon S3 only: the run folder the job writes to, e.g. `s3://bucket/prefix/2026-10-09_140312/`.
   * Omitted for runs not submitted yet and for other destinations.
   */
  deliveryLocation?: string;
}

/** BigQuery-specific destination configuration within a version. */
export interface BigQueryVersionConfig {
  type: 'bigquery';
  dataset: string;
  table: string;
  writePreference: ExportWritePreference | string;
  /**
   * Attachable PII from GET destination detail. Stored next to dataset/table,
   * not merged into `ExportDestinationVersion.fields`.
   */
  piiAttach?: Pick<PiiAttachPayload, 'fields'> & Partial<PiiAttachPayload>;
}

/** DSP-specific destination configuration within a version. */
export interface DspVersionConfig {
  type: 'dsp';
  segmentName: string;
  /** DSP+ agency the export runs under; absent on exports saved before it was stored. */
  agencyId?: string;
  agencyName?: string;
}

/**
 * Meta destination configuration from GET destination detail.
 * `audienceName` is optional. The detail view does not read `customAudienceName`.
 */
export interface MetaVersionConfig {
  type: 'meta';
  audienceName?: string | null;
  audienceId: string;
  accountId: string;
  customerFileSource: string;
}

/**
 * One Trade Desk account saved on an export version.
 * `rate` is that version's snapshot, not the current Connections rate.
 */
export interface TradeDeskRateTarget {
  accountId: string;
  name: string;
  idType: TradeDeskIdType;
  externalId: string;
  rate: TradeDeskAccountRate | null;
}

/**
 * Trade Desk destination configuration within a version.
 * `ttlDays`, `identityMode`, and `segmentPath` come from GET destination detail
 * and stay off the Destination Config rows.
 */
export interface TradeDeskVersionConfig {
  type: 'tradedesk';
  ttlDays?: number;
  identityMode?: string;
  segmentPath?: string;
  /** Absent or empty when this version stored no rate targets. */
  rateTargets?: TradeDeskRateTarget[];
}

/**
 * Amazon S3 destination configuration within a version (SPYG-2627).
 * `connectionName` is the connection's current name, or "Deleted connection";
 * bucket and prefix are stored on the version, so they survive a delete.
 */
export interface AmazonS3VersionConfig {
  type: 'amazonS3';
  connectionId?: string;
  connectionName?: string;
  bucketName?: string;
  prefix?: string;
  folder?: string;
  fileName?: string;
  fileFormat?: string;
  compression?: string;
  /** Attachable PII, stored next to the S3 settings as for BigQuery. */
  piiAttach?: Pick<PiiAttachPayload, 'fields'> & Partial<PiiAttachPayload>;
}

/** Discriminated union for destination-specific configuration within a version. */
export type DestinationVersionConfig =
  | BigQueryVersionConfig
  | DspVersionConfig
  | MetaVersionConfig
  | TradeDeskVersionConfig
  | AmazonS3VersionConfig;

/** A single version of an export destination, reflecting one snapshot of audience criteria. */
export interface ExportDestinationVersion {
  version: number;
  audienceName: string;
  audienceCriteria: string;
  isLatest: boolean;
  destinationConfig: DestinationVersionConfig;
  /** Null for DSP destinations which do not have exported fields. */
  fields: ExportedField[] | null;
  /** Total job count for this version; full list loaded via Endpoint 3. */
  jobCount: number;
}

/** Destination detail returned by GET /exports/{exportId}/destinations/{destinationId}. */
export interface ExportDestinationDetail {
  destinationId: string;
  name: string;
  icon: string;
  audience: string;
  /** Null for immediate (one-time) exports that have no recurring schedule. */
  scheduling: DestinationSchedule | null;
  versions: ExportDestinationVersion[];
}

/** Lightweight destination stub returned in the destinations list by Endpoint 1. */
export interface ExportDestinationStub {
  destinationId: string;
  name: string;
  iconId: string;
}

/** Top-level export detail returned by GET /exports/{exportId}. */
export interface ExportDetail {
  id: string;
  name: string;
  audienceId: string;
  /** Audience display name — resolved server-side; no longer derived from destination[0]. */
  audienceName: string;
  createdDate: string;
  initialJobDate: string | null;
  destinations: ExportDestinationStub[];
}

export interface GetExportDetailResponse {
  export: ExportDetail;
}

/** Response type for GET /exports/{exportId}/destinations/{destinationId}. */
export interface GetExportDestinationDetailResponse {
  destination: ExportDestinationDetail;
}

/** Response type for GET /exports/{exportId}/destinations/{destinationId}/versions/{version}/jobs. */
export interface ExportVersionJobsResponse {
  jobs: JobRun[];
  pagination: PaginationApiType;
}

/** Client-side state shape for a single version's jobs, keyed by `${destinationId}-${version}`. */
export interface ExportVersionJobsState {
  jobs: JobRun[];
  pagination: PaginationApiType | null;
  loading: boolean;
}

/** Payload emitted when the user reschedules a destination. */
export interface ReschedulePayload {
  exportType: ExportType;
  ttlDays: number | null;
  scheduledConfig: {
    scheduleType: ScheduledType;
    startDate: string;
    endDate: string | null;
    time: string;
    frequency: FrequencyValues | null;
    /** IANA timezone string selected by the user. */
    timezone: string;
  } | null;
}

/** Scheduling block within the wire-format reschedule request body. */
export interface RescheduleScheduledConfigPayload {
  scheduleType: ScheduledType;
  startDate: string;
  endDate: string | null;
  time: string;
  frequency: FrequencyValues | null;
  timezone: string;
}

/**
 * Request body for `POST /tenants/{tenantId}/exports/{exportId}/reschedule`.
 * `destinationId` identifies which destination branch to reschedule.
 * `scheduledConfig` is null when `exportType` is immediate. `ttl` is included
 * only when the destination is DSP (backend requires it in that case only).
 */
export interface RescheduleExportApiPayload {
  destinationId: string;
  exportType: ExportType;
  scheduledConfig: RescheduleScheduledConfigPayload | null;
  ttl?: number;
}

/**
 * Response returned by the reschedule endpoint. `jobId` is the new immediate
 * job id when `exportType` is immediate, or null when a scheduled job was armed.
 */
export interface RescheduleExportApiResponse {
  id: string;
  jobId: string | null;
}
